import React, { useContext, useRef, useState } from 'react';
import { Button, Menu, MenuItem } from '@material-ui/core';
import ArrowRight from '@material-ui/icons/ArrowRight';
import {
  chunk,
  cond,
  flatten,
  map,
  matchesProperty,
  property,
} from 'lodash-es';
import { asyncMap, perform, nCompose } from '@mummssoftware/utils/utils';
import { getVisitsByIds } from '@mummssoftware/utils/pimReader';
import { Loader, PrintProgressLoader } from '@mummssoftware/common-ui';
import { createPortal } from 'react-dom';
import { GridApi } from 'ag-grid-community';
import { VisitsGlobalContext } from '@mummssoftware/global-context';
import { formatDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import { AppContext } from '../../context';
import { SummaryFilterContext } from '../App/SummaryFilters';
import {
  crossPatientVisitSummary,
  download,
  downloadDetails,
  downloadSummary,
  saveAsCsv,
  saveAsExcel,
  visitDetailsProgress,
  visitSummaryFileName,
} from '../../translations/en.json';
import { downloadDetailsCSV, downloadDetailsExcel } from './downloadDetails';

type DownloadMenuProps = {
  gridApi: GridApi;
  style?: any;
};

type ExpandMenuItemProps = {
  label: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
};

const ExpandMenuItem = ({
  label,
  open,
  onToggle,
  onClose,
  children,
}: ExpandMenuItemProps) => {
  const anchorRef = useRef<HTMLLIElement>(null);
  return (
    <>
      <MenuItem ref={anchorRef} onClick={onToggle}>
        {label}
        <ArrowRight />
      </MenuItem>
      <Menu
        anchorEl={anchorRef.current || undefined}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={open && Boolean(anchorRef.current)}
        onClose={onClose}
        style={{ pointerEvents: 'none' }}
      >
        <div style={{ pointerEvents: 'auto' }}>{children}</div>
      </Menu>
    </>
  );
};

const otherwise = () => true;

const asyncFlatten = (p: Promise<any[][]>) => p.then(flatten);

const outputEmpty = () => [];

const visitCallBatchSize = Number(
  window.env.config.pimreaderServiceMaxVisitsPerCall,
);

const chunkIds = (ids: number[]) => chunk(ids, visitCallBatchSize);

const isTotal = matchesProperty('firstname', 'Total');

const toVisitIds = cond<mumms.VisitSummaryDTO, number[]>([
  [isTotal, outputEmpty],
  [otherwise, property('visitids')],
]);

const mapToVisitIds = (data: mumms.VisitSummaryDTO[]) => map(data, toVisitIds);

export const DownloadMenu = ({ gridApi, style }: DownloadMenuProps) => {
  const { agency, siteId, keycloak } = useContext(AppContext);
  const { hasPendingFilters } = useContext(SummaryFilterContext);
  const { fromDate, toDate } = useContext(VisitsGlobalContext);

  const summaryFileName = visitSummaryFileName
    .replace('{fromDate}', formatDateMMDDYYYY(fromDate))
    .replace('{toDate}', formatDateMMDDYYYY(toDate));

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [openSubmenu, setOpenSubmenu] = useState<'summary' | 'details' | null>(
    null,
  );

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpenSubmenu(null);
  };

  const [loading, setLoading] = useState(false);
  const [visitsDownloaded, setVisitsDownloaded] = useState<number>(0);
  const [visitsSelected, setVisitsSelected] = useState<number>(0);

  const downloadProgress = visitsSelected && visitsDownloaded / visitsSelected;

  const increaseProgress = (b: number) => {
    setVisitsDownloaded((a) => a + b);
  };

  const resetProgress = () => {
    setVisitsDownloaded(0);
    setVisitsSelected(0);
  };

  const getGridData = () => {
    const rowData: mumms.VisitSummaryDTO[] = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) rowData.push(node.data);
    });
    return rowData;
  };

  const getVisitsForChunk = (ids: number[]): Promise<mumms.pimreaderVisit[]> =>
    getVisitsByIds(
      ids,
      siteId as string,
      agency,
      keycloak as Keycloak.KeycloakInstance,
    ).then((res) => {
      increaseProgress(res.length);
      return res;
    });

  const visitsFromChunks = (
    idChunks: number[][],
  ): Promise<mumms.pimreaderVisit[][]> =>
    asyncMap(idChunks, getVisitsForChunk, 1);

  const recordCount = (ids: number[]) => {
    setVisitsSelected(ids.length);
  };

  const mapToVisitDtos = (ids: number[]): Promise<mumms.pimreaderVisit[]> =>
    nCompose([asyncFlatten, visitsFromChunks, chunkIds], ids);

  const toVisitDtos = (
    data: mumms.VisitSummaryDTO[],
  ): Promise<mumms.pimreaderVisit[]> =>
    nCompose(
      [mapToVisitDtos, perform(recordCount), flatten, mapToVisitIds],
      data,
    );

  const exportSummaryCsv = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({ allColumns: true, fileName: summaryFileName });
    }
  };

  const exportSummaryExcel = () => {
    if (gridApi) {
      gridApi.exportDataAsExcel({
        allColumns: true,
        fileName: summaryFileName,
      });
    }
  };

  const runDetailsExport = (kind: 'csv' | 'excel') => {
    const gridData = getGridData();
    if (siteId && keycloak && gridData) {
      setLoading(true);
      toVisitDtos(gridData)
        .then((visits) => {
          resetProgress();
          if (kind === 'csv') downloadDetailsCSV(visits);
          else downloadDetailsExcel(visits);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const getLoader = () =>
    downloadProgress ? (
      createPortal(
        <PrintProgressLoader
          progress={{
            percent: downloadProgress,
            message: visitDetailsProgress
              .replace('{downloaded}', String(visitsDownloaded))
              .replace('{selected}', String(visitsSelected)),
          }}
          sourceString={crossPatientVisitSummary}
        />,
        document.getElementById('portal-container') || document.body,
      )
    ) : (
      <Loader />
    );

  return (
    <>
      {loading && getLoader()}
      <Button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        style={style}
        disabled={hasPendingFilters}
        onClick={handleClick}
      >
        {download}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <ExpandMenuItem
          label={downloadSummary}
          open={openSubmenu === 'summary'}
          onToggle={() =>
            setOpenSubmenu(openSubmenu === 'summary' ? null : 'summary')
          }
          onClose={() => setOpenSubmenu(null)}
        >
          <MenuItem
            onClick={() => {
              exportSummaryCsv();
              handleClose();
            }}
          >
            {saveAsCsv}
          </MenuItem>
          <MenuItem
            onClick={() => {
              exportSummaryExcel();
              handleClose();
            }}
          >
            {saveAsExcel}
          </MenuItem>
        </ExpandMenuItem>
        <ExpandMenuItem
          label={downloadDetails}
          open={openSubmenu === 'details'}
          onToggle={() =>
            setOpenSubmenu(openSubmenu === 'details' ? null : 'details')
          }
          onClose={() => setOpenSubmenu(null)}
        >
          <MenuItem
            onClick={() => {
              runDetailsExport('csv');
              handleClose();
            }}
          >
            {saveAsCsv}
          </MenuItem>
          <MenuItem
            onClick={() => {
              runDetailsExport('excel');
              handleClose();
            }}
          >
            {saveAsExcel}
          </MenuItem>
        </ExpandMenuItem>
      </Menu>
    </>
  );
};
